import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StatusEnum } from "../../../../common/data/base/enums/statusEnum";
import MESSAGES from "../../../../common/data/constants/messages";
import { ICustomer } from "../../../../common/data/dtos/customer";
import { IService } from "../../../../common/data/dtos/service";
import { IWorkOrderByCollectionId, IWorkOrderEvidenceByCollectionId, IWorkOrderServiceByCollectionId } from "../../../../common/data/dtos/work-order-by-collection-id";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardActions, CardBody, CardFooter, CardFooterRight, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Select from "../../../../components/bootstrap/forms/Select";
import Textarea from "../../../../components/bootstrap/forms/Textarea";
import Spinner from "../../../../components/bootstrap/Spinner";
import { ConfirmationModal } from "../../../../components/extras/ConfirmationModal";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import customerService from "../../../../services/customer-service";
import fileService from "../../../../services/file-service";
import servicePriceService from "../../../../services/service-price-service";
import serviceService from "../../../../services/service-service";
import workOrderEvidenceService from "../../../../services/work-order-evidence-service";
import workOrderService from "../../../../services/work-order-service";
import workOrderServiceService from "../../../../services/work-order-service-service";
import LoadingPage from "../../loading/LoadingPage";
import { WorkOrderEvidenceModal } from "./CommonWorkOrderEvidenceModal";
import { WorkOrderDetailModal } from "./WorkOrderDetailModal";

type WorkOrderFormValues = {
    id?: string;
    customerId: string;
    serviceType: string;
    segmentStartDate: string;
    segmentStartLocation: string;
    segmentEndDate: string,
    segmentEndLocation: string;
    tower: string;
    lineName: string;
    notes: string;
}

type WorkOrderServiceFormValues = {
    workOrderId: string;
    servicePrice: number;
    totalPerformed: number;
    serviceId: string;
}

export const CommonWorkOrderDetail = () => {

    const { id } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalEvidenceOpened, setIsModalEvidenceOpened] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    const [workOrder, setWorkOrder] = useState<IWorkOrderByCollectionId | undefined>(undefined);
    const [customers, setCustomers] = useState<ICustomer[]>([]);
    const [services, setServices] = useState<IService[]>([]);

    const [beforeEvidence, setBeforeEvidence] = useState<string>();
    const [afterEvidence, setAfterEvidence] = useState<string>();
    const [evidenceToDelete, setEvidenceToDelete] = useState<string>();

    const formik = useFormik({
        initialValues: {
            customerId: '',
            serviceType: '',
            created: '',
            segmentStartDate: '',
            segmentStartLocation: '',
            segmentEndDate: '',
            segmentEndLocation: '',
            tower: '',
            lineName: '',
            notes: '',
        },
        validate: (values) => {
            const errors: {
                customerId?: string;
                serviceType?: string;
                segmentStartDate?: string;
                segmentStartLocation?: string;
                segmentEndDate?: string;
                segmentEndLocation?: string;
                tower?: string;
                lineName?: string;
                notes?: string;
            } = {};

            if (!values.customerId)
                errors.customerId = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.serviceType)
                errors.serviceType = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.segmentStartDate)
                errors.segmentStartDate = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.segmentStartLocation)
                errors.segmentStartLocation = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.segmentEndDate)
                errors.segmentEndDate = MESSAGES.VALIDATION.REQUIRED_FIELD;
            if (!values.segmentEndLocation)
                errors.segmentEndLocation = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const formikService = useFormik({
        initialValues: {
            workOrderId: '',
            serviceId: '',
            servicePrice: 0,
            totalPerformed: 0
        },
        onSubmit: (values, { resetForm }) => {
            onFormServiceSubmit(values);
            resetFormService();
        },
    });

    useEffect(() => {
        if (id) {
            resetForm();
            fetchData();
        }
    }, []);

    const fetchData = async () => {
        if (!id) {
            showWarning('Ordem de serviço não encontrada');
            return;
        }
        setIsLoading(true);

        try {

            const [workOrderResponse, customersResponse, servicesResponse] = await Promise.all([
                workOrderService.getWorkOrderGetByCollectionId([id]),
                customerService.getCustomers(),
                serviceService.getServices()
            ]);

            if (workOrderResponse.error || !workOrderResponse.workOrders) {
                showWarning(workOrderResponse.error || 'Ordem de serviço não encontrada');
                return;
            }

            if (customersResponse.error) {
                showWarning(customersResponse.error);
                return;
            }

            if (servicesResponse.error) {
                showWarning(servicesResponse.error);
                return;
            }

            setWorkOrder(workOrderResponse.workOrders[0] || undefined);
            setCustomers(customersResponse.customers || []);
            setServices(servicesResponse.services || []);

            setFieldValues(workOrderResponse.workOrders[0]);
            formikService.setFieldValue('serviceId', '');
        } catch (error) {
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    }

    const onFormSubmit = async (values: WorkOrderFormValues) => {
        if (!workOrder) {
            showWarning('Ordem de serviço não encontrada');
            return;
        }

        setIsSubmitting(true);

        const response = await workOrderService.updateWorkOrder({
            id: id,
            customerId: values.customerId,
            serviceType: values.serviceType,
            segmentStartDate: dayjs(values.segmentStartDate).toDate(),
            segmentStartLocation: values.segmentStartLocation,
            segmentEndDate: dayjs(values.segmentEndDate).toDate(),
            segmentEndLocation: values.segmentEndLocation,
            tower: values.tower,
            lineName: values.lineName,
            notes: values.notes,
            totalAmount: 0,
            rowVersion: workOrder.rowVersion,
            status: StatusEnum.Active
        });

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.EDIT_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.EDITED);
        await onAfterServiceSubmit();
    }

    const resetForm = () => {
        formik.resetForm({
            values: {
                customerId: '',
                serviceType: '',
                created: '',
                segmentStartDate: '',
                segmentStartLocation: '',
                segmentEndDate: '',
                segmentEndLocation: '',
                tower: '',
                lineName: '',
                notes: '',
            },
        });
    };

    const resetFormService = () => {
        formikService.resetForm({
            values: {
                workOrderId: '',
                serviceId: '',
                servicePrice: 0,
                totalPerformed: 0
            },
        });
    }

    const setFieldValues = (values: IWorkOrderByCollectionId) => {
        formik.setFieldValue('serviceType', values.serviceType || '');
        formik.setFieldValue('customerId', values.customerId || '');
        formik.setFieldValue('segmentStartDate', values.segmentStartDate ? dayjs(values.segmentStartDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('segmentStartLocation', values.segmentStartLocation || '');
        formik.setFieldValue('segmentEndDate', values.segmentEndDate ? dayjs(values.segmentEndDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('segmentEndLocation', values.segmentEndLocation || '');
        formik.setFieldValue('tower', values.tower || '');
        formik.setFieldValue('lineName', values.lineName || '');
        formik.setFieldValue('notes', values.notes || '');
    }

    const onFormServiceSubmit = async (values: WorkOrderServiceFormValues) => {

        const isOrderInvalid = !id ||
            !values.serviceId ||
            !values.servicePrice ||
            !values.totalPerformed;

        if (isOrderInvalid) {
            showWarning('Ordem de serviço não encontrada ou incompleta');
            return;
        }

        setIsSubmitting(true);

        const response = await workOrderServiceService.createWorkOrderService({
            workOrderId: id,
            totalPerformed: values.totalPerformed,
            serviceId: values.serviceId,
            servicePrice: values.servicePrice
        });

        setIsSubmitting(false);

        if (response.error) {
            showWarning(MESSAGES.ACTION.SAVE_ERROR);
            return;
        }

        showSuccess(MESSAGES.ACTION.SAVED);
        resetFormService();
        onAfterServiceSubmit();

    }

    const handleDeleteClick = async (values: IWorkOrderServiceByCollectionId) => {

        if (!values.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await workOrderServiceService.deleteWorkOrderService(values.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await onAfterServiceSubmit();
    }

    const onAfterServiceSubmit = async () => {
        if (id) {
            await fetchWorkOrderServices(id);
        }
    };

    const fetchWorkOrderServices = async (workOrderId: string) => {

        setIsLoading(true);

        const response = await workOrderService.getWorkOrderGetByCollectionId([workOrderId]);

        setIsLoading(false);

        if (response.error || !response.workOrders) {
            showWarning(response.error || 'Ordem de serviço não encontrada');
            return;
        }

        setWorkOrder(response.workOrders[0] || undefined);
        setFieldValues(response.workOrders[0]);
    };

    const toggleModal = (isVisible: boolean) => {
        setIsModalOpened(isVisible);
    };

    const toggleEvidenceModal = (isVisible: boolean) => {
        setIsModalEvidenceOpened(isVisible);
    };

    const onModalClose = () => {
        toggleModal(false);
    };

    const onModalEvidenceClose = () => {
        toggleEvidenceModal(false);
    };

    const onAfterSubmit = async () => {
        onModalClose();
    };

    const handleServiceChange = async (serviceId: string) => {
        const customerId = formik.values.customerId;

        if (!customerId || !serviceId) {
            showWarning('Selecione um cliente e um serviço para recuperar o valor do serviço');
            return;
        }

        setIsSubmitting(true);

        const response = await servicePriceService.getByCustomer(customerId, serviceId);

        setIsSubmitting(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        formikService.setFieldValue('servicePrice', response.servicePrice?.price || 0);
    }

    const fetchEvidence = async (selectedWorkOrderEvidence: IWorkOrderEvidenceByCollectionId) => {

        try {
            if (!selectedWorkOrderEvidence) {
                showWarning('Evidências não encontradas');
                return;
            }

            setAfterEvidence('');
            setBeforeEvidence('');

            const beforeKey = getKey(selectedWorkOrderEvidence?.beforeShot);
            const afterkey = getKey(selectedWorkOrderEvidence?.afterShot);

            if (!beforeKey && !afterkey) {
                showWarning('Chaves das evidências não encontradas');
                return;
            }

            setIsLoading(true);

            const promises = [
                beforeKey ? fileService.getEvidenceImageUrl(beforeKey) : Promise.resolve({ url: '', error: null }),
                afterkey ? fileService.getEvidenceImageUrl(afterkey) : Promise.resolve({ url: '', error: null })
            ];

            const [beforeResponse, afterResponse] = await Promise.all(promises);

            //setIsSubmitting(false);

            if (beforeResponse.error) {
                showWarning(beforeResponse.error);
                return;
            }

            if (afterResponse.error) {
                showWarning(afterResponse.error);
                return;
            }

            setBeforeEvidence(beforeResponse.url || '');
            setAfterEvidence(afterResponse.url || '');

            toggleModal(true);

        } catch {
            showWarning('Falha ao recuperar evidências')
        } finally {
            setIsLoading(false);
        }
    }

    const getKey = (url?: string) => {

        if (!url) {
            return;
        }

        const parsedUrl = new URL(url);
        const path = parsedUrl.pathname;
        const key = path.substring(path.lastIndexOf('/') + 1);

        return key;
    }

    const onAfterEvidenceSubmit = async () => {
        try {

            if (!id) {
                showWarning('Ordem de serviço não encontrada');
                return;
            }

            setIsLoading(true);

            const response = await workOrderService.getWorkOrderGetByCollectionId([id]);

            if (response.error || !response.workOrders) {
                showWarning(response.error || 'Ordem de serviço não encontrada');
                return;
            }

            setWorkOrder(response.workOrders[0] || undefined);
            onModalEvidenceClose();
        } catch (error) {
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    }

    const handleDeleteEvidenceClick = async () => {

        try {
            if (!evidenceToDelete) {
                showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
                return;
            }

            setIsLoading(true);

            const response = await workOrderEvidenceService.deleteWorkOrderEvidence(evidenceToDelete);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            await onAfterServiceSubmit();

        } catch (error) {
            showWarning(MESSAGES.ACTION.DELETE_ERROR);
        } finally {
            setIsLoading(false);
            onConfirmationModalClose();
        }
    }

    const onConfirmationModalClose = () => {
        setIsConfirmationModalOpen(false);
        setEvidenceToDelete('');
    }

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Card>
                <CardHeader borderSize={1}>
                    <CardLabel icon='InsertDriveFile' iconColor='primary'>
                        <CardTitle tag='div' className='h5'>
                            Ordem de Serviço
                        </CardTitle>
                    </CardLabel>
                </CardHeader>

                <CardBody>
                    <div className='row g-4'>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                label="Tipo"
                                isFloating
                            >
                                <Select
                                    id='serviceType'
                                    ariaLabel='Tipo'
                                    placeholder='Tipo'
                                    list={[
                                        { value: 'SE', text: 'SE - Subestação' },
                                        { value: 'LD', text: 'LD - Linha de Distribuição' },
                                        { value: 'LT', text: 'LT - Linha de Transmissão' },
                                        { value: 'PCH', text: 'PCH - Pequena Central Hidrelétrica' },
                                    ]}
                                    onChange={formik.handleChange}
                                    value={formik.values.serviceType}
                                />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                label="Cliente"
                                isFloating
                            >
                                <Select
                                    id='customerId'
                                    ariaLabel='Cliente'
                                    placeholder='Cliente'
                                    list={[
                                        { value: '', text: 'Selecione um cliente' },
                                        ...customers.map((customer) => ({
                                            value: customer.id,
                                            text: `${customer.name}`
                                        }))
                                    ]}
                                    onChange={formik.handleChange}
                                    value={formik.values.customerId}
                                />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="segmentStartDate"
                                label="Data de Início do Trecho"
                                isFloating
                            >
                                <Input
                                    type="date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.segmentStartDate} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="segmentStartLocation"
                                label="Coordenadas de Início do Trecho"
                                isFloating
                            >
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.segmentStartLocation} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="segmentEndDate"
                                label="Data de Fim do Trecho"
                                isFloating
                            >
                                <Input
                                    type="date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.segmentEndDate} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="segmentEndLocation"
                                label="Coordenadas de Fim do Trecho"
                                isFloating
                            >
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.segmentEndLocation} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="tower"
                                label="Torre"
                                isFloating
                            >
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.tower} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-6 col-lg-6 col-md-12 col-sm-12'>
                            <FormGroup
                                id="lineName"
                                label="Nome do Ramal"
                                isFloating
                            >
                                <Input
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.lineName} />
                            </FormGroup>
                        </div>

                        <div className='col-xl-12 col-lg-12 col-md-12 col-sm-12'>
                            <FormGroup
                                id="notes"
                                label="Observações"
                                isFloating
                            >
                                <Textarea
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.notes}
                                    className={'h-100'} />
                            </FormGroup>
                        </div>
                    </div>
                </CardBody>
                <CardFooter className='bg-transparent'>
                    <CardFooterRight>
                        <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                            {isSubmitting && <Spinner isSmall inButton isGrow />}
                            Salvar
                        </Button>
                    </CardFooterRight>
                </CardFooter>
            </Card>

            <Card>
                <CardHeader borderSize={1}>
                    <CardLabel icon='DesignServices' iconColor='primary'>
                        <CardTitle tag='div' className='h5'>
                            Serviços
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color='success' className='w-100' onClick={formikService.handleSubmit} isDisable={isSubmitting}>
                            {isSubmitting && <Spinner isSmall inButton isGrow />}
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className='table-responsive'>
                    <div className='row g-4'>

                        <div className='col-xl-4 col-lg-4 col-md-12 col-sm-12'>
                            <FormGroup
                                label="Serviço"
                                isFloating
                            >
                                <Select
                                    id='serviceId'
                                    ariaLabel='Serviço'
                                    placeholder='Serviço'
                                    list={[
                                        { value: '', text: 'Selecione um serviço' },
                                        ...services.map((service) => ({
                                            value: service.id,
                                            text: `${service.description}`
                                        }))
                                    ]}
                                    onChange={(e: { target: { value: string } }) => {
                                        formikService.handleChange(e);
                                        handleServiceChange(e.target.value);
                                    }}
                                    value={formikService.values.serviceId}
                                />
                            </FormGroup>
                        </div>

                        <div className='col-xl-4 col-lg-4 col-md-12 col-sm-12'>
                            <FormGroup id='servicePrice' label='Preço' isFloating>
                                <Input
                                    type="number"
                                    onChange={formikService.handleChange}
                                    onBlur={formikService.handleBlur}
                                    value={formikService.values.servicePrice}
                                    isValid={formikService.isValid}
                                    isTouched={formikService.touched.servicePrice}
                                    invalidFeedback={formikService.errors.servicePrice}
                                    validFeedback=''
                                />
                            </FormGroup>
                        </div>

                        <div className='col-xl-4 col-lg-4 col-md-12 col-sm-12'>
                            <FormGroup id='totalPerformed' label='Produção' isFloating>
                                <Input
                                    onChange={formikService.handleChange}
                                    onBlur={formikService.handleBlur}
                                    value={formikService.values.totalPerformed}
                                    isValid={formikService.isValid}
                                    isTouched={formikService.touched.totalPerformed}
                                    invalidFeedback={formikService.errors.totalPerformed}
                                    validFeedback=''
                                />
                            </FormGroup>
                        </div>

                        <div>
                            <table className='table table-modern table-hover'>
                                <thead>
                                    <tr>
                                        <th>Serviço</th>
                                        <th>Preço (R$)</th>
                                        <th>Produção</th>
                                        <th>Total (R$)</th>
                                        <td aria-labelledby='Actions' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {workOrder?.workOrderServices.map((item: IWorkOrderServiceByCollectionId) => (
                                        <tr key={item.id}>

                                            <td>{item?.description ?? '-'}</td>
                                            <td>{item?.price ?? '-'}</td>
                                            <td>{item?.totalPerformed ?? '-'}</td>
                                            <td>{item?.totalServiceAmount ?? '-'}</td>

                                            <td className='text-end'>
                                                <Button
                                                    color='dark'
                                                    isLight
                                                    icon='Delete'
                                                    onClick={() => handleDeleteClick(item)}
                                                    aria-label='Remover'
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                        </div>

                    </div>
                </CardBody>
            </Card>

            <Card>
                <CardHeader borderSize={1}>
                    <CardLabel icon='Image' iconColor='primary'>
                        <CardTitle tag='div' className='h5'>
                            Evidências
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color='success' className='w-100' onClick={() => toggleEvidenceModal(true)} >
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className='table-responsive'>
                    <div className='row g-4'>

                        <div>
                            <table className='table table-modern table-hover'>
                                <thead>
                                    <tr>
                                        <th>Data Evidência Antes</th>
                                        <th>Localização Antes</th>
                                        <th>Data Evidência Depois</th>
                                        <th>Localização Depois</th>
                                        <td aria-labelledby='Actions' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {workOrder?.evidences.map((item: IWorkOrderEvidenceByCollectionId) => (
                                        <tr key={item.id}>
                                            <td>{item.beforeShotDate ? dayjs(item.beforeShotDate).format('DD/MM/YYYY') : '-'}</td>
                                            <td>{item?.beforeShotLocation ?? '-'}</td>
                                            <td>{item.afterShotDate ? dayjs(item.afterShotDate).format('DD/MM/YYYY') : '-'}</td>
                                            <td>{item?.afterShotLocation ?? '-'}</td>


                                            <MoreActions
                                                id={item.id}
                                                showEdit={false}
                                                showDelete={true}
                                                onDeleteClick={() => {
                                                    setEvidenceToDelete(item.id);
                                                    setIsConfirmationModalOpen(true);
                                                }}
                                                customActions={[
                                                    {
                                                        label: 'Visualizar',
                                                        icon: 'RemoveRedEye',
                                                        onClickAction: () => fetchEvidence(item),
                                                    }
                                                ]}
                                            />
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </CardBody>
            </Card>

            <WorkOrderDetailModal
                isModalOpened={isModalOpened}
                onModalClose={onModalClose}
                onAfterSubmit={onAfterSubmit}
                beforeEvidence={beforeEvidence}
                afterEvidence={afterEvidence}
            />

            <WorkOrderEvidenceModal
                isModalOpened={isModalEvidenceOpened}
                onModalClose={onModalEvidenceClose}
                onAfterSubmit={onAfterEvidenceSubmit}
                workOrderId={id ?? ''}
            />

            <ConfirmationModal
                isModalOpened={isConfirmationModalOpen}
                onConfirm={handleDeleteEvidenceClick}
                title="Confirmar remoção"
                message="Deseja remover a evidência?"
                isSubmitting={isSubmitting}
                onModalClose={onConfirmationModalClose}
            />
        </>
    );
}